import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Eye, User, CreditCard, Coins, Loader2, LogOut, TrendingUp, Mail, Send, X, Bell } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { API } from '../utils/helpers';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';

export default function AdminPage() {
  const [adminAuth, setAdminAuth] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [readings, setReadings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [trends, setTrends] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [creditModal, setCreditModal] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [pushSubscribers, setPushSubscribers] = useState({ active: 0, total: 0 });
  const [pushForm, setPushForm] = useState({ title: '', body: '', url: '/', segment: 'all' });
  const [pushSending, setPushSending] = useState(false);
  const [pushHistory, setPushHistory] = useState([]);

  useEffect(() => { checkAdminAuth(); }, []);

  const checkAdminAuth = async () => {
    try { const res = await fetch(`${API}/admin/me`, { credentials: 'include' }); if (res.ok) { setAdminAuth(true); loadStats(); loadUsers(); loadTrends(); } } catch (e) {}
    setCheckingAuth(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginLoading(true); setLoginError('');
    try {
      const res = await fetch(`${API}/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(loginForm) });
      if (res.ok) { setAdminAuth(true); loadStats(); loadUsers(); loadTrends(); }
      else { const data = await res.json(); setLoginError(data.detail || 'Error de autenticacion'); }
    } catch (e) { setLoginError('Error de conexion'); }
    setLoginLoading(false);
  };

  const handleLogout = async () => { await fetch(`${API}/admin/logout`, { method: 'POST', credentials: 'include' }); setAdminAuth(false); };
  const loadStats = async () => { try { const res = await fetch(`${API}/admin/stats`, { credentials: 'include' }); if (res.ok) setStats(await res.json()); } catch (e) {} };
  const loadUsers = async (search = '') => { try { const res = await fetch(`${API}/admin/users?search=${encodeURIComponent(search)}&limit=100`, { credentials: 'include' }); if (res.ok) { const data = await res.json(); setUsers(data.users); setUsersTotal(data.total); } } catch (e) {} };
  const loadReadings = async () => { try { const res = await fetch(`${API}/admin/readings?limit=100`, { credentials: 'include' }); if (res.ok) setReadings((await res.json()).readings); } catch (e) {} };
  const loadTransactions = async () => { try { const res = await fetch(`${API}/admin/transactions?limit=100`, { credentials: 'include' }); if (res.ok) setTransactions((await res.json()).transactions); } catch (e) {} };
  const loadTrends = async () => { try { const res = await fetch(`${API}/admin/trends`, { credentials: 'include' }); if (res.ok) setTrends((await res.json()).trends); } catch (e) {} };
  const updateCredits = async (userId, newCredits) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/credits`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ credits: newCredits }) });
      if (res.ok) { toast.success(`Créditos actualizados a ${newCredits}`); loadUsers(searchQuery); setCreditModal(null); }
      else toast.error('Error al actualizar créditos');
    } catch { toast.error('Error de conexión'); }
  };
  const handleTabChange = (tab) => { setActiveTab(tab); if (tab === 'readings' && readings.length === 0) loadReadings(); if (tab === 'transactions' && transactions.length === 0) loadTransactions(); if (tab === 'emails' && !emailConfig) loadEmailConfig(); if (tab === 'push') loadPushData(); };
  const loadPushData = async () => {
    try {
      const [subRes, histRes] = await Promise.all([
        fetch(`${API}/admin/push/subscribers`, { credentials: 'include' }),
        fetch(`${API}/admin/push/history`, { credentials: 'include' })
      ]);
      if (subRes.ok) setPushSubscribers(await subRes.json());
      if (histRes.ok) setPushHistory((await histRes.json()).broadcasts || []);
    } catch (e) {}
  };
  const sendPushBroadcast = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) { toast.error('Completa titulo y mensaje'); return; }
    setPushSending(true);
    try {
      const res = await fetch(`${API}/admin/push/broadcast`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(pushForm)
      });
      const data = await res.json();
      if (res.ok) {
        if (data.sent > 0) toast.success(`Enviados: ${data.sent} / ${data.total}${data.failed > 0 ? ` (${data.failed} fallidos)` : ''}`);
        else toast.warning(`No hay suscriptores activos en el segmento "${pushForm.segment}"`);
        setPushForm({ ...pushForm, title: '', body: '' });
        loadPushData();
      } else {
        toast.error(data.detail || 'Error al enviar notificaciones');
      }
    } catch (e) { toast.error('Error de conexion'); }
    setPushSending(false);
  };
  const loadEmailConfig = async () => { try { const res = await fetch(`${API}/admin/email/config`, { credentials: 'include' }); if (res.ok) setEmailConfig(await res.json()); } catch (e) {} loadEmailHistory(); };
  const loadEmailHistory = async () => { try { const res = await fetch(`${API}/admin/email/history`, { credentials: 'include' }); if (res.ok) setEmailHistory((await res.json()).campaigns); } catch (e) {} };
  const sendMassEmail = async () => {
    const cleanContent = emailContent.replace(/<(.|\n)*?>/g, '').trim();
    if (!emailSubject || !cleanContent) { toast.error('Completa el asunto y el contenido'); return; }
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/admin/email/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ subject: emailSubject, content: emailContent, target: 'all' }) });
      if (res.ok) {
        const data = await res.json();
        if (data.sent > 0 && data.failed === 0) toast.success(`Emails enviados correctamente: ${data.sent} de ${data.total}`);
        else if (data.sent > 0) toast.warning(`Enviados: ${data.sent}, Fallidos: ${data.failed} de ${data.total}`);
        else toast.error(`Envio fallido: 0 enviados de ${data.total}. Verifica la configuracion de Brevo.`);
        setEmailSubject(''); setEmailContent(''); loadEmailHistory();
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Error al enviar emails'); }
    } catch (e) { toast.error('Error de conexion al enviar emails'); }
    setSendingEmail(false);
  };

  if (checkingAuth) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;

  if (!adminAuth) {
    return (
      <div className="mystic-bg min-h-screen flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="text-center mb-8">
            <img src="https://customer-assets.emergentagent.com/job_mystic-cards-56/artifacts/wfhpmaw5_Madame-Blavatsky--Logo-2_20260216_193202_0000.png" alt="Madame Blavatsky" className="w-20 h-20 mx-auto mb-4" />
            <h1 className="font-decorative text-2xl text-purple-300" data-testid="admin-login-title">Administracion</h1>
            <p className="text-[var(--text-muted)] text-sm mt-1">Panel de control de Madame Blavatsky</p>
          </div>
          <form onSubmit={handleLogin} className="glass-card p-6 rounded-xl border border-gold-300/10 space-y-4">
            <div>
              <label className="text-[var(--text-muted)] text-sm mb-1 block">Email</label>
              <input type="email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} className="mystic-input w-full" placeholder="admin@mblavatsky.com" required data-testid="admin-email-input" />
            </div>
            <div>
              <label className="text-[var(--text-muted)] text-sm mb-1 block">Contrasena</label>
              <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="mystic-input w-full" placeholder="••••••••" required data-testid="admin-password-input" />
            </div>
            {loginError && <p className="text-red-400 text-sm" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" disabled={loginLoading} className="btn-gold w-full" data-testid="admin-login-btn">
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Entrar'}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: Star },
    { id: 'trends', label: 'Tendencias', icon: TrendingUp },
    { id: 'users', label: 'Usuarios', icon: User },
    { id: 'readings', label: 'Lecturas', icon: Eye },
    { id: 'transactions', label: 'Pagos', icon: CreditCard },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'push', label: 'Push', icon: Bell },
  ];

  return (
    <div className="min-h-screen py-6 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-purple-400" data-testid="admin-title">Panel de Administracion</h1>
          <div className="flex gap-2">
            <a href="/" className="btn-ghost text-sm" data-testid="admin-back">Volver al sitio</a>
            <button onClick={handleLogout} className="px-3 py-1 rounded-lg text-sm bg-red-600/20 text-red-400 hover:bg-red-600/30" data-testid="admin-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-purple-600 text-white' : 'bg-white/5 text-[var(--text-muted)] hover:bg-white/10'}`}
              data-testid={`admin-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Usuarios', value: stats.users.total, sub: `+${stats.users.recent_signups} (30d)`, color: 'text-purple-400' },
                { label: 'Lecturas', value: stats.readings.total, sub: `+${stats.readings.recent_7d} (7d)`, color: 'text-blue-400' },
                { label: 'Ingresos', value: `$${stats.payments.revenue}`, sub: `${stats.payments.paid} pagos`, color: 'text-green-400' },
                { label: 'Creditos', value: stats.credits_in_circulation, sub: `${stats.natal_charts} cartas natales`, color: 'text-yellow-400' },
              ].map((card, i) => (
                <div key={i} className="glass-card p-4" data-testid={`stat-card-${i}`}>
                  <p className="text-[var(--text-muted)] text-xs mb-1">{card.label}</p>
                  <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                  <p className="text-[var(--text-muted)] text-xs mt-1">{card.sub}</p>
                </div>
              ))}
            </div>
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4">Lecturas por tipo</h3>
              <div className="space-y-2">
                {stats.readings.by_type.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5">
                    <span className="text-[var(--text-secondary)] text-sm">{r.type || 'Sin tipo'}</span>
                    <span className="text-purple-400 font-bold">{r.count}</span>
                  </div>
                ))}
                {stats.readings.by_type.length === 0 && <p className="text-[var(--text-muted)] text-sm">No hay lecturas aun</p>}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4">Usuarios Nuevos (30 dias)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#9ca3af', fontSize: 10 }} 
                    interval="preserveStartEnd"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value) => value ? value.slice(5) : ''}
                  />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} allowDecimals={false} width={40} />
                  <Tooltip contentStyle={{ background: '#1a1025', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 8, color: '#e2e8f0' }} />
                  <Area type="monotone" dataKey="users" stroke="#a855f7" fill="rgba(168,85,247,0.2)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4">Lecturas por Dia (30 dias)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#9ca3af', fontSize: 10 }} 
                    interval="preserveStartEnd"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value) => value ? value.slice(5) : ''}
                  />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} allowDecimals={false} width={40} />
                  <Tooltip contentStyle={{ background: '#1a1025', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 8, color: '#e2e8f0' }} />
                  <Bar dataKey="readings" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4">Ingresos (30 dias)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#9ca3af', fontSize: 10 }} 
                    interval="preserveStartEnd"
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tickFormatter={(value) => value ? value.slice(5) : ''}
                  />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} width={50} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ background: '#1a1025', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 8, color: '#e2e8f0' }} formatter={(v) => [`$${v}`, 'Ingresos']} />
                  <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="rgba(34,197,94,0.15)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {trends.length === 0 && <p className="text-[var(--text-muted)] text-center py-8">No hay datos de tendencias aun</p>}
          </div>
        )}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <input type="text" placeholder="Buscar por nombre o email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && loadUsers(searchQuery)} className="mystic-input flex-1" data-testid="admin-user-search" />
              <button onClick={() => loadUsers(searchQuery)} className="btn-ghost text-sm">Buscar</button>
            </div>
            <p className="text-[var(--text-muted)] text-sm">{usersTotal} usuarios en total</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/10 text-[var(--text-muted)]"><th className="text-left p-3">Usuario</th><th className="text-left p-3">Email</th><th className="text-center p-3">Creditos</th><th className="text-center p-3">Lecturas</th><th className="text-left p-3">Registro</th><th className="text-center p-3">Acciones</th></tr></thead>
                <tbody>
                  {users.map((u, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3"><div className="flex items-center gap-2">{u.picture && <img src={u.picture} alt="" className="w-6 h-6 rounded-full" />}<span className="text-[var(--text-secondary)]">{u.name}</span></div></td>
                      <td className="p-3 text-[var(--text-muted)]">{u.email}</td>
                      <td className="p-3 text-center"><span className="credit-badge"><Coins className="w-3 h-3" /> {u.credits}</span></td>
                      <td className="p-3 text-center text-[var(--text-muted)]">{u.reading_count}</td>
                      <td className="p-3 text-[var(--text-muted)] text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('es') : '-'}</td>
                      <td className="p-3 text-center">
                        <button onClick={() => { setCreditModal(u); setCreditAmount(String(u.credits || 0)); }}
                          className="px-3 py-1 rounded text-xs bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 transition-colors" data-testid={`edit-credits-${i}`}>
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {activeTab === 'readings' && (
          <div className="space-y-2">
            {readings.map((r, i) => (
              <div key={i} className="glass-card p-4 flex items-center justify-between">
                <div><p className="text-[var(--text-secondary)] font-medium">{r.spread_type}</p><p className="text-[var(--text-muted)] text-xs">{r.user_id} · {r.created_at ? new Date(r.created_at).toLocaleString('es') : '-'}</p></div>
                <span className="credit-badge"><Coins className="w-3 h-3" /> {r.credits_used || 0}</span>
              </div>
            ))}
            {readings.length === 0 && <p className="text-[var(--text-muted)] text-center py-8">No hay lecturas aun</p>}
          </div>
        )}
        {activeTab === 'transactions' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-[var(--text-muted)]"><th className="text-left p-3">ID</th><th className="text-left p-3">Usuario</th><th className="text-center p-3">Creditos</th><th className="text-center p-3">Monto</th><th className="text-center p-3">Estado</th><th className="text-left p-3">Fecha</th></tr></thead>
              <tbody>
                {transactions.map((t, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 text-[var(--text-muted)] text-xs font-mono">{t.transaction_id}</td>
                    <td className="p-3 text-[var(--text-muted)]">{t.user_id}</td>
                    <td className="p-3 text-center text-purple-400">{t.credits}</td>
                    <td className="p-3 text-center text-green-400">${t.amount}</td>
                    <td className="p-3 text-center"><span className={`px-2 py-1 rounded text-xs ${t.payment_status === 'paid' ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>{t.payment_status}</span></td>
                    <td className="p-3 text-[var(--text-muted)] text-xs">{t.created_at ? new Date(t.created_at).toLocaleString('es') : '-'}</td>
                  </tr>
                ))}
                {transactions.length === 0 && <tr><td colSpan={6} className="text-[var(--text-muted)] text-center py-8">No hay transacciones aun</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            {emailConfig && !emailConfig.configured && (
              <div className="glass-card p-6 border border-yellow-500/30">
                <p className="text-yellow-400 font-medium mb-2">Email no configurado</p>
                <p className="text-[var(--text-muted)] text-sm">Agrega <code className="text-purple-300">BREVO_API_KEY</code> y <code className="text-purple-300">SENDER_EMAIL</code> en el archivo <code className="text-purple-300">backend/.env</code> para habilitar el envio de emails.</p>
              </div>
            )}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2"><Send className="w-5 h-5" /> Enviar Email Masivo</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">Asunto</label>
                  <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} placeholder="Asunto del email..." className="mystic-input w-full" data-testid="email-subject" />
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">Contenido</label>
                  <RichTextEditor value={emailContent} onChange={setEmailContent} placeholder="Escribe el contenido del email..." />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[var(--text-muted)] text-xs">Se enviara a todos los usuarios registrados</p>
                  <button onClick={sendMassEmail} disabled={sendingEmail || !emailSubject} className="btn-gold flex items-center gap-2" data-testid="send-email-btn">
                    {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {sendingEmail ? 'Enviando...' : 'Enviar a Todos'}
                  </button>
                </div>
              </div>
            </div>
            {emailHistory.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-purple-300 mb-4">Historial de Campanhas</h3>
                <div className="space-y-3">
                  {emailHistory.map((c, i) => (
                    <div key={i} className="p-4 rounded-lg bg-white/5 flex items-center justify-between">
                      <div>
                        <p className="text-[var(--text-secondary)] font-medium">{c.subject}</p>
                        <p className="text-[var(--text-muted)] text-xs">{c.created_at ? new Date(c.created_at).toLocaleString('es') : '-'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 text-sm">{c.sent} enviados</p>
                        {c.failed > 0 && <p className="text-red-400 text-xs">{c.failed} fallidos</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'push' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-4">
                <p className="text-[var(--text-muted)] text-xs mb-1">Suscriptores activos</p>
                <p className="text-3xl font-bold text-purple-400" data-testid="push-active-count">{pushSubscribers.active}</p>
                <p className="text-[var(--text-muted)] text-xs mt-1">de {pushSubscribers.total} totales</p>
              </div>
              <div className="glass-card p-4 flex flex-col justify-center">
                <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                  Envia notificaciones push a tus usuarios. Solo recibiran quienes activaron notificaciones en su dispositivo.
                </p>
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-purple-300 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5" /> Nueva Notificacion Push
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Titulo <span className="text-[var(--text-muted)] text-xs">({pushForm.title.length}/60)</span>
                  </label>
                  <input
                    type="text"
                    value={pushForm.title}
                    onChange={e => setPushForm({ ...pushForm, title: e.target.value.slice(0, 60) })}
                    placeholder="Tu carta del dia te espera"
                    className="mystic-input w-full"
                    data-testid="push-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[var(--text-muted)] mb-1">
                    Mensaje <span className="text-[var(--text-muted)] text-xs">({pushForm.body.length}/250)</span>
                  </label>
                  <textarea
                    value={pushForm.body}
                    onChange={e => setPushForm({ ...pushForm, body: e.target.value.slice(0, 250) })}
                    placeholder="Los astros tienen un mensaje especial para ti hoy. Descubrelo ahora."
                    rows={3}
                    className="mystic-input w-full resize-none"
                    data-testid="push-body-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">URL de destino</label>
                    <select
                      value={pushForm.url}
                      onChange={e => setPushForm({ ...pushForm, url: e.target.value })}
                      className="mystic-input w-full"
                      data-testid="push-url-select"
                    >
                      <option value="/">Inicio</option>
                      <option value="/dashboard">Dashboard</option>
                      <option value="/tarot">Tarot</option>
                      <option value="/astrology">Carta Natal</option>
                      <option value="/synastry">Sinastria</option>
                      <option value="/numerology">Numerologia</option>
                      <option value="/iching">I Ching</option>
                      <option value="/credits">Comprar Creditos</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-[var(--text-muted)] mb-1">Segmento</label>
                    <select
                      value={pushForm.segment}
                      onChange={e => setPushForm({ ...pushForm, segment: e.target.value })}
                      className="mystic-input w-full"
                      data-testid="push-segment-select"
                    >
                      <option value="all">Todos los suscriptores</option>
                      <option value="members">Solo miembros premium</option>
                      <option value="free">Solo usuarios gratuitos</option>
                      <option value="test">Solo prueba (yo)</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-lg bg-white/5 p-3 border border-purple-400/10">
                  <p className="text-xs text-[var(--text-muted)] mb-1">Vista previa:</p>
                  <div className="flex items-start gap-2 p-2 rounded bg-black/40">
                    <img src="/assets/icon-192.png" alt="" className="w-10 h-10 rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{pushForm.title || 'Titulo de la notificacion'}</p>
                      <p className="text-xs text-white/70 line-clamp-2">{pushForm.body || 'Contenido de la notificacion...'}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-[var(--text-muted)] text-xs">
                    {pushForm.segment === 'test'
                      ? 'Solo tu la recibiras (modo prueba)'
                      : `Se enviara a suscriptores activos del segmento`}
                  </p>
                  <button
                    onClick={sendPushBroadcast}
                    disabled={pushSending || !pushForm.title.trim() || !pushForm.body.trim()}
                    className="btn-gold flex items-center gap-2"
                    data-testid="push-send-btn"
                  >
                    {pushSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                    {pushSending ? 'Enviando...' : 'Enviar Notificacion'}
                  </button>
                </div>
              </div>
            </div>

            {pushHistory.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-purple-300 mb-4">Historial de Envios</h3>
                <div className="space-y-3">
                  {pushHistory.map((h, i) => (
                    <div key={i} className="p-4 rounded-lg bg-white/5 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--text-secondary)] font-medium">{h.title}</p>
                        <p className="text-[var(--text-muted)] text-xs mt-1">{h.body}</p>
                        <div className="flex gap-2 mt-1 text-xs text-[var(--text-muted)]">
                          <span>{h.sent_at ? new Date(h.sent_at).toLocaleString('es') : '-'}</span>
                          <span>·</span>
                          <span>{h.segment}</span>
                          <span>·</span>
                          <span>{h.url}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-green-400 text-sm">{h.sent} enviadas</p>
                        {h.failed > 0 && <p className="text-red-400 text-xs">{h.failed} fallidas</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Credit Edit Modal */}
        {creditModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCreditModal(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-6 rounded-xl border border-purple-400/20 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-purple-300">Gestionar Creditos</h3>
                <button onClick={() => setCreditModal(null)} className="text-[var(--text-muted)] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="mb-4">
                <p className="text-[var(--text-secondary)] font-medium">{creditModal.name}</p>
                <p className="text-[var(--text-muted)] text-sm">{creditModal.email}</p>
                <p className="text-yellow-400 text-sm mt-1">Balance actual: {creditModal.credits} creditos</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm text-[var(--text-muted)] mb-1">Nuevo balance de creditos</label>
                <input type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} min="0"
                  className="mystic-input w-full text-lg" data-testid="credit-amount-input" />
              </div>
              <div className="flex gap-2 mb-4">
                {[5, 10, 25, 50].map(n => (
                  <button key={n} onClick={() => setCreditAmount(String((parseInt(creditAmount) || 0) + n))}
                    className="px-3 py-1.5 rounded-lg text-xs bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors">+{n}</button>
                ))}
                <button onClick={() => setCreditAmount(String(Math.max(0, (parseInt(creditAmount) || 0) - 12)))}
                  className="px-3 py-1.5 rounded-lg text-xs bg-red-600/20 text-red-400 hover:bg-red-600/30 transition-colors">-12</button>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setCreditModal(null)} className="flex-1 py-2 rounded-lg bg-white/5 text-[var(--text-muted)] hover:bg-white/10 transition-colors text-sm">Cancelar</button>
                <button onClick={() => updateCredits(creditModal.user_id, parseInt(creditAmount) || 0)}
                  className="flex-1 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors text-sm font-medium" data-testid="save-credits-btn">
                  Guardar ({creditAmount} creditos)
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
